import { createAsyncThunk } from '@reduxjs/toolkit';
import { createGenericSlice, GenericState } from '../GenericSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import { SECRETS_ENV } from '../../Config/firebase';
import axios from 'axios';

// Define the Google Meet state interface
export interface GoogleMeetData {
    isAuthenticated: boolean;
    userEmail: string | null;
    userName: string | null;
    accessToken?: string | null;
}

// Local storage key for Google Meet state persistence
const GOOGLE_MEET_STORAGE_KEY = 'google_meet_auth_state';

// Helper function to load persisted state
const loadPersistedState = (): GoogleMeetData => {
    try {
        const persisted = localStorage.getItem(GOOGLE_MEET_STORAGE_KEY);
        if (persisted) {
            const parsed = JSON.parse(persisted);
            // Only use persisted state if it's not too old (24 hours)
            const maxAge = 24 * 60 * 60 * 1000; // 24 hours
            if (parsed.timestamp && (Date.now() - parsed.timestamp) < maxAge) {
                console.log('📦 Loading persisted Google Meet state:', parsed.data);
                console.log('📅 Persisted state age:', Math.round((Date.now() - parsed.timestamp) / (1000 * 60)), 'minutes');
                return parsed.data;
            } else {
                console.log('🗑️ Persisted Google Meet state is too old, clearing');
                console.log('📅 Persisted state age:', Math.round((Date.now() - parsed.timestamp) / (1000 * 60)), 'minutes (max: 1440)');
                localStorage.removeItem(GOOGLE_MEET_STORAGE_KEY);
            }
        } else {
            console.log('📭 No persisted Google Meet state found');
        }
    } catch (error) {
        console.error('Error loading persisted Google Meet state:', error);
        localStorage.removeItem(GOOGLE_MEET_STORAGE_KEY);
    }
    return {
        isAuthenticated: false,
        userEmail: null,
        userName: null,
        accessToken: null,
    };
};

// Helper function to save state to localStorage
const savePersistedState = (state: GoogleMeetData) => {
    try {
        const dataToPersist = {
            data: state,
            timestamp: Date.now()
        };
        localStorage.setItem(GOOGLE_MEET_STORAGE_KEY, JSON.stringify(dataToPersist));
        console.log('💾 Persisting Google Meet state:', state);
    } catch (error) {
        console.error('Error saving Google Meet state to localStorage:', error);
    }
};

// Helper function to clear persisted state
export const clearPersistedGoogleMeetState = () => {
    try {
        localStorage.removeItem(GOOGLE_MEET_STORAGE_KEY);
        console.log('🗑️ Cleared persisted Google Meet state');
    } catch (error) {
        console.error('Error clearing Google Meet state from localStorage:', error);
    }
};

// Helper function to check if user has been away for a long time
const isLongAbsence = (lastCheckTime: number): boolean => {
    const longAbsenceThreshold = 6 * 60 * 60 * 1000; // 6 hours
    return (Date.now() - lastCheckTime) > longAbsenceThreshold;
};

// Utility function to check if authentication check is needed
export const shouldCheckGoogleMeetAuth = (): boolean => {
    try {
        const persisted = localStorage.getItem(GOOGLE_MEET_STORAGE_KEY);
        if (!persisted) {
            return true; // No persisted state, need to check
        }

        const parsed = JSON.parse(persisted);
        if (!parsed.timestamp) {
            return true; // No timestamp, need to check
        }

        // Check if it's been more than 1 hour since last check
        const oneHour = 60 * 60 * 1000;
        if ((Date.now() - parsed.timestamp) > oneHour) {
            return true; // Been more than an hour, need to check
        }

        // If we have valid persisted state and it's recent, no need to check
        return false;
    } catch (error) {
        console.error('Error checking if Google Meet auth is needed:', error);
        return true; // On error, check anyway
    }
};

// Utility function to get current persisted state for debugging
export const getCurrentPersistedGoogleMeetState = () => {
    try {
        const persisted = localStorage.getItem(GOOGLE_MEET_STORAGE_KEY);
        if (persisted) {
            const parsed = JSON.parse(persisted);
            return {
                data: parsed.data,
                timestamp: parsed.timestamp,
                age: parsed.timestamp ? Math.round((Date.now() - parsed.timestamp) / (1000 * 60)) : null
            };
        }
        return null;
    } catch (error) {
        console.error('Error getting persisted Google Meet state:', error);
        return null;
    }
};

const initialState: GenericState<GoogleMeetData> = {
    data: loadPersistedState(),
    status: 'not_loaded',
    error: null,
};

const API_ENDPOINT = SECRETS_ENV.API_END_POINT;

// Check Google Meet authentication status
export const checkGoogleMeetAuth = createAsyncThunk(
    'googleMeet/checkAuth',
    async (serviceProviderId: string) => {
        try {
            console.log('🔍 Starting Google Meet auth check for:', serviceProviderId);
            
            // Check if this is a long absence and we should force a fresh check
            const persisted = localStorage.getItem(GOOGLE_MEET_STORAGE_KEY);
            if (persisted) {
                const parsed = JSON.parse(persisted);
                if (parsed.timestamp && isLongAbsence(parsed.timestamp)) {
                    console.log('⏰ Long absence detected, forcing fresh Google Meet authentication check');
                }
            }
            
            const response = await axios.post(`${API_ENDPOINT}/api/gmeet/check-auth`, {
                userId: serviceProviderId
            });

            const authData = response.data;
            console.log('✅ Google Meet auth check response:', authData);

            const result = {
                isAuthenticated: authData.isAuthenticated,
                userEmail: authData.email || authData.providerEmail || null,
                userName: authData.name && authData.name.trim() ? authData.name : null,
                accessToken: authData.accessToken || null
            };

            savePersistedState(result);
            return result;
        } catch (error) {
            console.error('❌ Error checking Google Meet auth:', error);
            const result = {
                isAuthenticated: false,
                userEmail: null,
                userName: null,
                accessToken: null
            };
            savePersistedState(result);
            return result;
        }
    }
);

// Revoke Google Meet access
export const revokeGoogleMeetAccess = createAsyncThunk(
    'googleMeet/revokeAccess',
    async (serviceProviderId: string) => {
        try {
            await axios.post(`${API_ENDPOINT}/api/gmeet/revoke-access`, {
                userId: serviceProviderId
            });

            const result = {
                isAuthenticated: false,
                userEmail: null,
                userName: null,
                accessToken: null
            };
            savePersistedState(result);
            return result;
        } catch (error) {
            console.error('Error revoking Google Meet access:', error);
            throw error;
        }
    }
);

// Selector for Google Meet access token
export const selectGoogleMeetAccessToken = (state: { googleMeetSlice: GenericState<GoogleMeetData> }) => 
    state.googleMeetSlice.data?.accessToken || null;

const googleMeetSlice = createGenericSlice({
    name: 'googleMeet',
    initialState,
    reducers: {
        reset: (state) => {
            state.data = {
                isAuthenticated: false,
                userEmail: null,
                userName: null,
                accessToken: null,
            };
            state.status = 'not_loaded';
            state.error = null;
            localStorage.removeItem(GOOGLE_MEET_STORAGE_KEY);
        },
        // Add a reducer to update state and persist it
        updateGoogleMeetState: (state, action: PayloadAction<Partial<GoogleMeetData>>) => {
            if (!state.data) return;
            
            const updatedData: GoogleMeetData = {
                isAuthenticated: action.payload.isAuthenticated ?? state.data.isAuthenticated,
                userEmail: action.payload.userEmail ?? state.data.userEmail,
                userName: action.payload.userName ?? state.data.userName,
                accessToken: action.payload.accessToken ?? state.data.accessToken,
            };
            state.data = updatedData;
            savePersistedState(updatedData);
        },
    },
    extraReducers: (builder: any) => {
        builder
            // Check Auth
            .addCase(checkGoogleMeetAuth.pending, (state: GenericState<GoogleMeetData>) => {
                state.status = 'loading';
            })
            .addCase(checkGoogleMeetAuth.fulfilled, (state: GenericState<GoogleMeetData>, action: PayloadAction<GoogleMeetData>) => {
                state.status = 'finished';
                state.data = action.payload;
                state.error = null;
                savePersistedState(action.payload);
            })
            .addCase(checkGoogleMeetAuth.rejected, (state: GenericState<GoogleMeetData>, action: { error: { message?: string } }) => {
                state.status = 'error';
                state.error = action.error?.message || 'Failed to check Google Meet authentication';
                // Don't clear persisted state on error, just update status
            })
            
            // Revoke Access
            .addCase(revokeGoogleMeetAccess.pending, (state: GenericState<GoogleMeetData>) => {
                state.status = 'loading';
            })
            .addCase(revokeGoogleMeetAccess.fulfilled, (state: GenericState<GoogleMeetData>, action: PayloadAction<GoogleMeetData>) => {
                state.status = 'finished';
                state.data = action.payload;
                state.error = null;
                savePersistedState(action.payload);
            })
            .addCase(revokeGoogleMeetAccess.rejected, (state: GenericState<GoogleMeetData>, action: { error: { message?: string } }) => {
                state.status = 'error';
                state.error = action.error?.message || 'Failed to revoke Google Meet access';
            });
    },
});

export const { reset: resetGoogleMeetState, updateGoogleMeetState } = googleMeetSlice.actions;
export default googleMeetSlice.reducer; 